import React, { useState, useEffect, useCallback } from 'react';
import {
  CloudOff, Download, Plus, Trash2, Check, ShieldCheck, Volume2, Loader2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { preloadPack, loadBank, countCachedAudios } from '../../lib/offline';
import { Field } from '../Shared';

export default function AdminOffline() {
  const [bank, setBank] = useState([]);
  const [q, setQ] = useState('');
  const [kw, setKw] = useState('');
  const [ans, setAns] = useState('');
  const [preload, setPreload] = useState(null); // null | {done,total} | 'done'
  const [genBusy, setGenBusy] = useState(null);
  const [cachedCount, setCachedCount] = useState(0);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    try {
      const entries = await api.get('/api/admin/canned_responses?kind=offline');
      setBank(entries);
      setCachedCount(await countCachedAudios(entries));
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!q.trim() || !ans.trim()) return;
    try {
      await api.post('/api/admin/canned_responses', {
        kind: 'offline', question: q.trim(), keywords: kw.trim(), answer: ans.trim(),
      });
      setQ(''); setKw(''); setAns('');
      refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  const rm = async (item) => {
    try {
      await api.del(`/api/admin/canned_responses/${item.id}`);
      refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  const genAudio = async (item) => {
    setGenBusy(item.id);
    setErr('');
    try {
      await api.post(`/api/admin/canned_responses/${item.id}/generate_audio`);
      refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setGenBusy(null);
    }
  };

  const startPreload = async () => {
    setErr('');
    setPreload({ done: 0, total: 1 });
    try {
      const result = await preloadPack((done, total) => setPreload({ done, total }));
      setPreload('done');
      setCachedCount(result.audios);
    } catch (e) {
      setErr(e.message);
      setPreload(null);
    }
  };

  const audioReady = bank.filter((e) => e.audio_available).length;
  const localBank = loadBank();

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <h3 className="panel-h"><CloudOff size={17} /> Banque de réponses hors ligne</h3>
        <p className="panel-p">
          Le plan B en cas de coupure : ces réponses (texte + audio pré-généré) sont conservées côté serveur,
          puis préchargées sur l'appareil. Si le réseau tombe, le chat bascule automatiquement dessus —
          matching par mots-clés sur la question posée.
        </p>

        <div className="preload-row">
          <div className="preload-stat">
            <b>{bank.length}</b> réponse{bank.length > 1 ? 's' : ''} en banque · <b>{audioReady}/{bank.length}</b> audio{audioReady > 1 ? 's' : ''} généré{audioReady > 1 ? 's' : ''} ·
            {' '}<b>{localBank.length}</b> texte{localBank.length > 1 ? 's' : ''} et <b>{cachedCount}</b> audio{cachedCount > 1 ? 's' : ''} sur cet appareil
          </div>
          {preload === null || preload === 'done' ? (
            <button className="btn-primary" onClick={startPreload}>
              <Download size={15} /> {preload === 'done' ? 'Précharger à nouveau' : 'Précharger sur cet appareil'}
            </button>
          ) : (
            <div className="preload-bar">
              <div className="preload-fill" style={{ width: `${Math.round((preload.done / Math.max(1, preload.total)) * 100)}%` }} />
              <span>{preload.done}/{preload.total} audios</span>
            </div>
          )}
          {preload === 'done' && <span className="test ok inline"><Check size={14} /> Pack disponible hors ligne</span>}
        </div>

        {err && <div className="test err">{err}</div>}

        <div className="bank-add">
          <div className="bank-add-row">
            <Field label="Question type" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ton plat préféré ?" />
            <Field label="Mots-clés de matching" value={kw} onChange={(e) => setKw(e.target.value)} placeholder="plat manger cuisine préféré" />
          </div>
          <Field label="Réponse (dans le ton d'Olivier)" value={ans} onChange={(e) => setAns(e.target.value)} area placeholder="Réponse courte et punchy…" />
          <button className="btn-primary" onClick={add} disabled={!q.trim() || !ans.trim()}><Plus size={15} /> Ajouter à la banque</button>
        </div>

        <div className="bank-list">
          {bank.map((e) => (
            <div key={e.id} className="bank-item">
              <div className="bank-q">{e.question}</div>
              <div className="bank-a">{e.answer}</div>
              <div className="bank-foot">
                <span className={`cache-chip${e.audio_available ? ' ok' : ''}`}>
                  {e.audio_available ? <><Check size={12} /> audio prêt</> : <><Download size={12} /> audio à générer</>}
                </span>
                {!e.audio_available && (
                  <button className="mini-btn" onClick={() => genAudio(e)} disabled={genBusy !== null}>
                    {genBusy === e.id ? <Loader2 size={13} className="spin" /> : <Volume2 size={13} />} générer
                  </button>
                )}
                {e.keywords && <span className="bank-kw">{e.keywords}</span>}
                <button className="mini-btn danger" onClick={() => rm(e)}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="sec-warn slim">
          <ShieldCheck size={15} />
          La génération audio utilise la voix clonée ElevenLabs (réglages → Voice ID) et met les mp3 en cache
          serveur ; le préchargement les copie dans le Cache Storage de cet appareil pour une lecture sans réseau.
        </div>
      </div>
    </div>
  );
}
